// S-LP (landingpage.md §D.5) — the landing page's white-label branding config.
// EVERY name / number / contact the landing sections render comes from this
// object, never hardcoded in JSX, so a second tenant/domain can rebrand by
// overriding config (identity, palette stays OKLCh tokens).
// The vitest white-label check renders the sections with `SAMPLE_ALT_BRAND`
// to prove nothing is baked in.
//
// S-I18N-branding — hero/CTA COPY moved out of this module (S-I18N-W1b
// follow-up, مالک-approved). It is locale-dependent text, so it cannot live
// in a plain module read once — it now comes from the `landing` i18n
// namespace (`home.hero.title`/`subtitle`, `home.cta.primary`/`secondary`/
// `dashboard`, `home.finalCta.title`) via `useTranslation(["landing.brand",
// "landing"])`. `landing.brand` is a small PER-DEPLOYMENT override namespace
// (`locales/{fa,ar}/landing.brand.json`, only the same 6 keys) that i18next
// checks first and falls through to `landing`'s defaults for any key it
// doesn't carry — an empty/absent override file is therefore exactly "use
// the default copy". `scripts/rebrand.ps1` populates that file for a
// rebrand; see docs/BRAND_RULES.md for the full contract.
//
// NOTE(honesty — landingpage.md §D.2 «صادق نسبت به امکانات واقعی»):
// `flags.trialBadge` / `flags.refundBadge` default OFF because the 7-day
// trial + subscription refund are NOT built yet (MS-S9 موج ۲ deferred them).
// When the owner ships the trial, flip the flags — copy updates itself.

import { BRAND } from "../brand/brand.config";

export interface LandingBranding {
  /** هویت */
  name: string; // نام کامل مؤسسه
  shortName: string; // نام کوتاه برای متن‌های میانی
  domain: string;
  /** آمار نوار اعتبار hero (ارقام لاتین؛ رندر با toFa) */
  stats: {
    faculties: number;
    programs: number;
    instructors: number;
    students: number;
  };
  /** تماس/اعتماد */
  contact: {
    email: string;
  };
  /** S-LAND-POLISH م۳ — مقصدِ CTA اصلی (کلیدِ مسیرِ `go()`). */
  primaryCtaRoute: string;
  /** S-LAND-POLISH م۴ — مقصدِ CTA وقتی بازدیدکننده لاگین کرده است (برچسبش
   *  اکنون `home.cta.dashboard` در i18n است، نه اینجا). */
  authedCta: { route: string };
  /** سوئیچ‌های صداقت — فقط وقتی قابلیت واقعاً ساخته شد روشن شوند. */
  flags: {
    trialBadge: boolean; // «۷ روز رایگان بدون کارت» (MS-S9 موج ۲)
    refundBadge: boolean; // «ضمانت بازگشت وجه ۷ روزه» (MS-S9 موج ۲)
  };
}

// S-REBRAND-EMI موج ۱: چهار فیلدِ هویتیِ زیر دیگر اینجا نوشته نمی‌شوند —
// از src/brand/brand.config.ts می‌آیند تا لندینگ و <head> و API یک نام
// داشته باشند. شکلِ interface و کپی/آمار/فلگ‌ها دست‌نخورده‌اند (تستِ
// white-label با SAMPLE_ALT_BRAND همچنان همان قرارداد را می‌سنجد).
export const BRANDING: LandingBranding = {
  name: BRAND.nameFullFa,
  shortName: BRAND.nameCompactFa,
  domain: BRAND.domain,
  stats: { faculties: 8, programs: 248, instructors: 94, students: 8400 },
  contact: { email: BRAND.email },
  /** م۳ — مقصدِ CTA اصلی: صفحهٔ ورود/ثبت‌نام (نه فرمِ درخواستِ پذیرش). */
  primaryCtaRoute: "login",
  /** م۴ — نسخهٔ کاربرِ لاگین‌کرده از همان CTA (برچسب: home.cta.dashboard). */
  authedCta: { route: "dashboard" },
  flags: { trialBadge: false, refundBadge: false },
};

/** برند نمونهٔ دوم — فقط برای تست white-label (S-LP-7)؛ در UI رندر نمی‌شود. */
export const SAMPLE_ALT_BRAND: LandingBranding = {
  name: "آکادمی نمونهٔ آزما",
  shortName: "آزما",
  domain: "example-academy.test",
  stats: { faculties: 3, programs: 42, instructors: 17, students: 950 },
  contact: { email: "hello@example-academy.test" },
  primaryCtaRoute: "login",
  authedCta: { route: "dashboard" },
  flags: { trialBadge: true, refundBadge: true },
};
